import { toast } from "sonner";

// Phones shoot 12–50 MP now; anything over ~9 MB is re-encoded as JPEG in the browser
// (long side capped at 4096px) so uploads stay under the server's 12 MB limit.
// HEIC is left alone — browsers can't decode it; the server converts it instead.
const MAX_BYTES = 9 * 1024 * 1024;
const MAX_SIDE = 4096;

const isRaster = (f) =>
  /^image\/(jpeg|png|webp|gif|bmp|tiff|avif)$/i.test(f?.type || "") ||
  /\.(jpe?g|png|webp|gif|bmp|tiff?|avif)$/i.test(f?.name || "");

const mb = (n) => `${(n / 1048576).toFixed(n >= 10485760 ? 0 : 1)} MB`;

const decode = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      /* fall through to <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};

export const shrinkImage = async (file, { maxBytes = MAX_BYTES, maxSide = MAX_SIDE } = {}) => {
  if (!file || !isRaster(file) || file.size <= maxBytes) return file;
  let bmp;
  try {
    bmp = await decode(file);
  } catch {
    return file;
  }
  const w = bmp.naturalWidth || bmp.width;
  const h = bmp.naturalHeight || bmp.height;
  let scale = Math.min(1, maxSide / Math.max(w, h));
  let quality = 0.9;
  try {
    for (let i = 0; i < 7; i++) {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(w * scale));
      canvas.height = Math.max(1, Math.round(h * scale));
      canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
      if (blob && blob.size <= maxBytes) {
        const out = new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", {
          type: "image/jpeg",
          lastModified: Date.now(),
        });
        out.enhanced = file.enhanced;
        out.shrunkFrom = file.size;
        return out;
      }
      if (quality > 0.72) quality -= 0.08;
      else scale *= 0.8;
    }
  } finally {
    bmp.close?.();
  }
  return file;
};

// Same, with a one-line heads-up so staff know why the file changed.
export const shrinkForUpload = async (file, opts) => {
  const out = await shrinkImage(file, opts);
  if (out !== file && out.shrunkFrom) {
    toast.message(`Photo resized ${mb(out.shrunkFrom)} → ${mb(out.size)} for upload`);
  }
  return out;
};

export const shrinkAll = (files, opts) =>
  Promise.all(Array.from(files || []).map((f) => shrinkForUpload(f, opts)));
